import { PREMIUM_MONTHLY_PRICE, PREMIUM_ANNUAL_PRICE, ANNUAL_PCT_OFF } from '@/data/pricingData'

/**
 * Profile field defaults used when normalizing a freshly-fetched profile row
 * into camelCase for the React tree. Centralized so UI components that read
 * these fields can also import a single source of truth instead of
 * re-hardcoding the fallback values.
 *
 * Consumed by ProfileContext's normalization memo.
 */
export const PROFILE_DEFAULTS = {
  preferredTranslation: 'KJV',
  debatedTopicsView: 'all_perspectives',
  responseDepth: 'auto',
  scriptureCitation: 'when_relevant',
  sermonStyle: 'expository',
  sermonFormat: 'both',
  highlightsEnabled: true,
  bibleInsightLength: 'medium',
}

export const DEFAULTS = {
  hero: {
    tagline: 'Scripture First. Always.',
    headline: 'Know What You Believe.\nKnow Why You Believe It.',
    description:
      'Study any passage, prepare sermons in minutes, and defend your faith with confidence \u2014 all guided by Scripture. Free for every Christian.',
    ctaText: 'Start Studying Free',
    ctaHref: '/login',
    subtext:
      'For all Christians \u2014 Baptist, Reformed, Catholic, non-denom. No theological agenda.',
    backgroundImage: '/Background1.webp',
  },

  features: {
    sectionTitle: 'How Illuminate Faith Helps You Grow',
    cards: [
      {
        iconName: 'Shield',
        title: 'Defend Your Faith with Confidence',
        description:
          'When someone challenges what you believe, be ready. Get clear, Scripture-grounded answers to the toughest questions about Christianity.',
      },
      {
        iconName: 'BookOpen',
        title: 'Go Deeper into Any Passage',
        description:
          'Understand the historical context, original languages, and theological meaning behind every verse \u2014 like having a commentary that talks back.',
      },
      {
        iconName: 'Users',
        title: 'Prepare Studies and Sermons in Minutes',
        description:
          'Build outlines, discussion questions, illustrations, and cross-references for your next sermon or small group \u2014 without hours of prep.',
      },
    ],
  },

  pricing: {
    title: 'Choose Your Plan',
    subtitle:
      'Start free with the essentials, or unlock the full power of Illuminate Faith with Premium.',
    basicName: 'Basic',
    basicPrice: 'Free',
    basicSubtext: 'Get started with the essentials',
    premiumName: 'Premium',
    premiumPrice: `$${PREMIUM_MONTHLY_PRICE}`,
    premiumPeriod: '/month',
    premiumYearlyPrice: `$${PREMIUM_ANNUAL_PRICE}/year`,
    premiumYearlySaving: `save ${ANNUAL_PCT_OFF}%`,
    premiumBadge: 'Recommended',
    basicCta: 'Get Started Free',
    premiumCta: 'Go Premium',
    features: [
      { name: 'Questions per day', basic: '5', premium: 'Unlimited' },
      { name: 'Basic apologetics answers', basic: true, premium: true },
      { name: 'Scripture verse lookup', basic: true, premium: true },
      { name: 'Community access', basic: true, premium: true },
      { name: 'Advanced apologetics with sources', basic: false, premium: true },
      { name: 'Full Scripture commentary with theological insights', basic: false, premium: true },
      {
        name: 'Bible study prep tools (outlines & discussion questions)',
        basic: false,
        premium: true,
      },
      { name: 'Cross-reference and context analysis', basic: false, premium: true },
      { name: 'Priority response speed', basic: false, premium: true },
      { name: 'Early access to new features', basic: false, premium: true },
    ],
  },

  subscribe: {
    title: 'Subscribe to Premium',
    subtitle: 'Unlock the full power of Illuminate Faith.',
    highlights: [
      'Unlimited questions',
      'Advanced apologetics with sources',
      'Full Scripture commentary',
      'Bible study prep tools',
      'Priority response speed',
    ],
    monthlyPrice: `$${PREMIUM_MONTHLY_PRICE}`,
    yearlyPrice: `$${PREMIUM_ANNUAL_PRICE}`,
    yearlySaving: `Save ${ANNUAL_PCT_OFF}%`,
    stripeMonthlyLink: 'https://buy.stripe.com/MONTHLY_LINK',
    stripeYearlyLink: 'https://buy.stripe.com/YEARLY_LINK',
    ctaText: 'Subscribe Now',
    footerText: 'Secure payment powered by Stripe. Cancel anytime.',
  },

  chat: {
    welcomeMessage: 'Welcome! Ask me anything about Scripture, faith, or the Christian life.',
    headerTitle: 'Illuminate Faith Chat',
    inputPlaceholder: 'Ask about a verse, a tough question, or anything on your heart...',
  },

  navbar: {
    logoText: 'Illuminate Faith',
    homeLinks: [
      { label: 'Features', href: '#features' },
      { label: 'Blog', href: '/blog' },
      { label: 'Try Free', href: '#signup-cta' },
      { label: 'Log In', href: '/login' },
    ],
    otherLinks: [
      { label: 'Home', href: '/' },
      { label: 'About', href: '/about' },
      { label: 'Blog', href: '/blog' },
    ],
  },

  footer: {
    logoText: 'Illuminate Faith',
    tagline: 'Scripture study, apologetics, and sermon prep for every Christian.',
    links: [
      { label: 'About', href: '/about' },
      { label: 'Blog', href: '/blog' },
      { label: 'Privacy', href: '/privacy' },
      { label: 'Terms', href: '/terms' },
      { label: 'Support & Feedback', href: '/support' },
    ],
  },

  privacy: {
    title: 'Privacy Policy',
    paragraphs: [
      'Illuminate Faith respects your privacy. When you create an account or subscribe to our newsletter, we collect your name and email address. We use this information solely to operate Illuminate Faith, send you updates about the platform, and communicate with you about your account.',
      'We will never sell, share, or rent your personal information to third parties. We won\u2019t spam you or use your email for anything other than Illuminate Faith communications.',
      'You can unsubscribe from newsletter communications at any time by clicking the link in any email we send.',
      'If you have questions about your data, contact us at support@illuminatefaithai.com.',
    ],
  },

  terms: {
    title: 'Terms of Service',
    paragraphs: [
      'By creating an account, subscribing to the newsletter, or otherwise using Illuminate Faith, you agree to these terms.',
      'Illuminate Faith is provided as-is without warranties. We reserve the right to modify these terms, the service, or pricing at any time. Material changes will be communicated by email when feasible.',
      'By subscribing to our newsletter, you agree to receive email communications from Illuminate Faith about new features and platform updates. You may unsubscribe at any time.',
      'We are not responsible for any technical issues or service interruptions, though we will work to resolve them promptly.',
      'You agree to use Illuminate Faith in compliance with all applicable laws and regulations. We reserve the right to suspend or terminate any account for misuse.',
    ],
  },
}
